<template>
  <!--
    «Условия платежа» (CONCIERGE_CHAT_FIRST.md §5.2, §5.7): the edition this card points to, read from its state in the
    poll. CURRENT — «Подтвердить условия» (the manager's prefilled method as is) and «Другой способ оплаты» (the method
    choice inside the card); CONFIRMED — «Отменить оплату» while the payment is cancellable. The design's `.case-terms`.
  -->
  <div class="cb" :class="{ 'cb--muted': muted }" data-testid="chat-block-CONCIERGE_TERMS" :data-state="state?.state">
    <div class="cb__title">{{ t('vitrine.concierge.terms.title') }}</div>
    <template v-if="state && state.amount != null">
      <div v-if="state.recipient" class="cb__row" data-testid="concierge-terms-recipient">
        <small>{{ t('vitrine.concierge.terms.recipient') }}</small>
        <span>{{ state.recipient }}</span>
      </div>
      <div class="cb__row" data-testid="concierge-terms-amount">
        <small>{{ t('vitrine.concierge.terms.amount') }}</small>
        <span class="num">{{ f.money(state.amount, state.currency) }} {{ state.currency }}</span>
      </div>
      <div v-if="state.method && !choosing" class="cb__row" data-testid="concierge-terms-method">
        <small>{{ t('vitrine.concierge.payMethodTitle') }}</small>
        <span>{{ t('vitrine.concierge.draft.kind.' + state.method) }}<template v-if="state.payInCurrency"> · {{ state.payInCurrency }}</template></span>
      </div>
      <div v-if="state.feePercent != null && !muted" class="cb__row" data-testid="concierge-terms-fee">
        <small>{{ t('vitrine.concierge.terms.feeShort', { fee: f.pct(state.feePercent) }) }}</small>
        <span class="cb__soft">{{ t('vitrine.concierge.terms.feeIncluded') }}</span>
      </div>
      <p v-if="current" class="cb__total">{{ t('vitrine.concierge.terms.amountByPartner') }}</p>
    </template>

    <!-- The method choice inside the card: the pay form's chips and the shared method fields (С-7 prefilled). -->
    <template v-if="current && state?.canConfirm && choosing">
      <div class="cb__chips" data-testid="concierge-pay-methods">
        <button v-for="k in payKinds" :key="k" type="button" class="cb__chip" :class="{ 'cb__chip--on': form.sourceKind === k }"
                :data-testid="'concierge-pay-method-' + k" @click="pickKind(k)">
          {{ t('vitrine.concierge.draft.kind.' + k) }}
        </button>
      </div>
      <PayMethodFields
        v-model:bank-name="form.bankName"
        v-model:bank-country="form.bankCountry"
        v-model:account-owned-by-client="form.accountOwnedByClient"
        v-model:account-last4="form.accountLast4"
        v-model:country="form.country"
        v-model:city="form.city"
        v-model:available-from="form.availableFrom"
        v-model:available-to="form.availableTo"
        v-model:comment="form.comment"
        v-model:currency="form.currency"
        class="cb__fields"
        :kind="form.sourceKind"
        :country-options="countryOptions"
        :currency-suggestions="fiat"
        :crypto-pair="CONCIERGE_CRYPTO"
        :cash-min-label="cashMinLabel"
        extended
        testid-prefix="concierge"
        bank-hint-key="vitrine.concierge.pay.bankHint"
        cash-hint-key="vitrine.concierge.pay.cashHint"
        data-testid="concierge-pay-form"
      />
      <button type="button" class="cb__primary" :disabled="disabled || !!problem" data-testid="concierge-pay"
              @click="confirmChosen">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
        {{ t('vitrine.concierge.terms.confirm') }}
      </button>
      <p v-if="problemText" class="cb__err" data-testid="concierge-pay-problem">{{ problemText }}</p>
      <button type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled" data-testid="concierge-pay-back"
              @click="choosing = false">{{ t('common.cancel') }}</button>
    </template>
    <template v-else-if="current && state?.canConfirm">
      <button type="button" class="cb__primary" :disabled="disabled" data-testid="chat-block-terms-confirm" @click="confirm">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
        {{ t('vitrine.concierge.terms.confirm') }}
      </button>
      <button type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled" data-testid="chat-block-terms-other"
              @click="openChoice()">{{ t('vitrine.concierge.terms.otherMethod') }}</button>
    </template>
    <!-- CURRENT, but pay() would refuse now (no card, the payment service is off…): no button, the manager writes. -->
    <div v-else-if="current" class="cb__state" data-testid="chat-block-state">{{ refusalText }}</div>

    <div v-if="stateKey" class="cb__state" data-testid="chat-block-state">{{ t(stateKey) }}</div>
    <!-- CONFIRMED: the payment can still be withdrawn until the requisites are released (cancellable). -->
    <button v-if="state?.state === 'CONFIRMED' && state.canCancel && state.paymentId" type="button" class="cb__danger"
            :disabled="disabled" data-testid="concierge-payment-cancel" @click="cancelPayment">
      {{ confirmCancel ? t('vitrine.concierge.cancelConfirm') : t('vitrine.concierge.payment.cancel') }}
    </button>
    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { translatedOr } from '@/utils/i18nLookup';
import PayMethodFields from '@/components/vitrine/PayMethodFields.vue';
import { useCapabilities } from '@/composables/useCapabilities';
import { payConciergeRequest } from '@/api/concierge';
import { cancelTopUp, topUpLimits, type SourceKind } from '@/api/topup';
import type { TermsBlockState } from '@/api/support';
import {
  CONCIERGE_CRYPTO, emptyPayForm, payFormForKinds, payFormFromDraft, payFormProblem, payKindsFor, switchPayKind,
  termsCardDraft, toPaymentBody, type PayForm,
} from '@/views/vitrine/concierge/conciergeUi';
import { useConciergeReference } from '@/views/vitrine/concierge/useConciergeReference';
import { termsStateKey } from '../chatBlocks';
import { useCardAction, useCardFormat } from './useCardAction';

const props = defineProps<{
  state?: TermsBlockState;
  /** The concierge request of this thread; null — the screen has not loaded it yet (no call can go). */
  requestId: string | null;
  taskClosed: boolean;
  /** Another call of the feed is in flight. */
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t } = useI18n();
const f = useCardFormat();
const { capabilities, conciergeCrypto } = useCapabilities();
const { fiat, countryOptions, loadReference } = useConciergeReference(t);
const { running, error, run } = useCardAction(() => props.reread());

const current = computed(() => props.state?.state === 'CURRENT');
const muted = computed(() => props.state?.state === 'SUPERSEDED' || props.state?.state === 'CLOSED');
const stateKey = computed(() => termsStateKey(props.state, props.taskClosed));
const disabled = computed(() => !!props.busy || running.value || !props.requestId);
/** Why CURRENT terms cannot be confirmed now — the server's code as the vitrine words it, else the general line. */
const refusalText = computed(() => {
  const code = props.state?.confirmRefusal;
  const key = code ? `vitrine.error.${code}` : '';
  return translatedOr(t, key, t('vitrine.concierge.terms.state.unavailable'));
});

/** The methods offered now — CRYPTO only while the server offers it; undefined until the server answered. */
const payKinds = computed<SourceKind[]>(() => payKindsFor(conciergeCrypto.value));
const offeredKinds = computed(() => (capabilities.value ? payKinds.value : undefined));

const choosing = ref(false);
const form = ref<PayForm>(emptyPayForm('BANK', ''));
const confirmCancel = ref(false);
/** The cash floor in the recipient's currency (the server checks the recipient's amount against it); null — none. */
const cashMin = ref<number | null>(null);

const recipientCurrency = computed(() => props.state?.currency ?? '');
const cashMinLabel = computed(() => (form.value.sourceKind === 'CASH' && cashMin.value != null
  ? `${f.money(cashMin.value, recipientCurrency.value)} ${recipientCurrency.value}` : null));
const belowCashMin = computed(() => form.value.sourceKind === 'CASH' && cashMin.value != null
  && props.state?.amount != null && Number(props.state.amount) < cashMin.value);
const problem = computed(() => (belowCashMin.value ? 'vitrine.topup.belowCashMin'
  : payFormProblem(form.value, offeredKinds.value)));
const problemText = computed(() => {
  const key = problem.value;
  if (!key) return '';
  return key === 'vitrine.topup.belowCashMin' ? t(key, { min: cashMinLabel.value ?? '' }) : t(key);
});

/** The manager's prefilled method on an offered method (a CRYPTO draft while crypto is off → the first offered one). */
function draftForm(): PayForm {
  const s = props.state;
  const base = payFormFromDraft(s ? termsCardDraft(s) : null, recipientCurrency.value);
  return capabilities.value ? payFormForKinds(base, payKinds.value, recipientCurrency.value) : base;
}

function openChoice(start: PayForm = draftForm()) {
  form.value = start;
  choosing.value = true;
  error.value = '';
  void loadReference();
}

function pickKind(kind: SourceKind) {
  form.value = switchPayKind(form.value, kind, recipientCurrency.value);
}

function send(body: PayForm) {
  const s = props.state;
  const id = props.requestId;
  if (!s || !id) return;
  void run(() => payConciergeRequest(id, toPaymentBody(body, s.version, s.draftVersion)))
    .then((ok) => { if (ok) choosing.value = false; });
}

/**
 * «Подтвердить условия»: the manager's method as is. Without a draft, or when the draft is not payable as it stands
 * (a method not offered, a missing field), the method choice opens instead — prefilled, with the reason shown.
 */
function confirm() {
  const s = props.state;
  if (!s) return;
  const draft = termsCardDraft(s);
  const start = draftForm();
  if (!draft || start.sourceKind !== draft.sourceKind || payFormProblem(start, offeredKinds.value)) {
    openChoice(start);
    return;
  }
  send(start);
}

function confirmChosen() {
  if (problem.value) return;
  send(form.value);
}

/** «Отменить оплату» — confirmed with a second tap (the payment is already with the payment service). */
function cancelPayment() {
  const paymentId = props.state?.paymentId;
  if (!paymentId) return;
  if (!confirmCancel.value) { confirmCancel.value = true; return; }
  void run(() => cancelTopUp(paymentId)).finally(() => { confirmCancel.value = false; });
}

/** The cash floor is known before the click (REQ-040): asked when cash is picked, in the recipient's currency. */
watch(() => [choosing.value, form.value.sourceKind, recipientCurrency.value] as const, async ([open, kind, currency]) => {
  if (!open || kind !== 'CASH' || !currency) { cashMin.value = null; return; }
  try {
    const l = await topUpLimits(currency);
    cashMin.value = l.cashMinimum == null ? null : Number(l.cashMinimum);
  } catch { cashMin.value = null; }   // honest-degrade: no hint rather than a guessed floor; the server still checks
});

// The card left CURRENT (paid elsewhere, replaced, closed): the choice closes with it.
watch(() => props.state?.state, (s) => {
  if (s !== 'CURRENT') choosing.value = false;
  confirmCancel.value = false;
});
</script>

<style scoped lang="scss">
@use './cards.scss';
</style>
